import { cn } from '@/lib/classMerge'
import Label from './Label'
import { ChangeEvent, HTMLInputTypeAttribute } from 'react'
import { cva, VariantProps } from 'class-variance-authority'
import FormError from './Forms/FormError'
import { FieldErrors } from 'react-hook-form'

export const inputStyles = cva('', {
  variants: {
    variant: {
      default: [
        'rounded-2xl bg-ui-lightGray px-4 py-[14px] text-base font-medium outline-none focus:border-blue-500'
      ],
      black: [
        'rounded-md bg-transparent px-4 py-[15px] text-base font-medium outline-none border border-gray-300 focus:border-ui-black'
      ],
      plain: [
        'rounded-lg bg-transparent px-4 py-3 text-sm font-medium outline-none border border-gray-200 focus:border-ui-black placeholder:text-neutral-500 placeholder:disabled:text-neutral-400'
      ],
      primary: [
        'w-[480px]',
        'rounded-md',
        'border',
        'border-[#2E6CFF]',
        'bg-white',
        'px-4',
        'py-[12px]',
        'text-base',
        'font-medium',
        'outline-none',
        'focus:ring-2',
        'focus:ring-blue-500',
        'focus:border-blue-500',
        '!bg-white'
      ],
      secondary: [
        'w-full',
        'rounded-md',
        'border',
        'border-gray-200',
        'bg-white',
        'px-4',
        'py-[12px]',
        'text-base',
        'font-medium',
        'outline-none',
        'focus:ring-2',
        'focus:ring-gray-200',
        'focus:border-gray-200',
        'placeholder:text-gray-400',
        '!bg-white'
      ]
    }
  },
  defaultVariants: {
    variant: 'default'
  }
})

export interface IInputProps extends VariantProps<typeof inputStyles> {
  type: HTMLInputTypeAttribute
  value: string
  onChange: (value: string) => void
  placeholder: string
  name: string
  disabled?: boolean
  id: string
  label?: string
  wrapperClassName?: string
  className?: string
  labelClassName?: string
  iconClassName?: string
  icon?: React.ElementType
  error?: string | FieldErrors<FormData>
  min?: number
}

export default function Input({
  placeholder,
  label,
  wrapperClassName,
  labelClassName,
  id,
  className,
  variant,
  onChange,
  disabled = false,
  icon: Icon,
  iconClassName,
  error,
  min,
  ...props
}: IInputProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className={cn('relative', wrapperClassName)}>
      {!!label && <Label text={label} className={labelClassName} />}
      <input
        id={id}
        placeholder={placeholder}
        className={cn(inputStyles({ variant }), 'w-full', className)}
        onChange={handleChange}
        {...props}
        disabled={disabled}
        min={min}
      />
      {Icon && (
        <Icon
          className={
            iconClassName
              ? iconClassName
              : 'absolute right-4 top-1/2 text-ui-gray'
          }
        />
      )}
      {!!error && <FormError wrapperClassName='mt-2' text={error} />}
    </div>
  )
}
